import React from "react";
import { api, formatPHP, formatPHTDate } from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "../components/ui/dialog";
import { Badge } from "../components/ui/badge";
import { toast } from "sonner";
import { ChevronLeft, ChevronRight, Plus, Trash2, Pencil, X, RefreshCw, ExternalLink } from "lucide-react";
import { format, addMonths, subMonths, startOfMonth } from "date-fns";

const CATEGORIES = [
  "Utilities",
  "Maintenance",
  "Staff / Wages",
  "Supplies",
  "Food & Beverage",
  "Cleaning",
  "Marketing",
  "Repairs",
  "Other",
];

export default function AdminFinance() {
  const [month, setMonth] = React.useState(startOfMonth(new Date()));
  const [summary, setSummary] = React.useState(null);
  const [expenses, setExpenses] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [breakdown, setBreakdown] = React.useState(null); // { type: string, title: string }
  const [breakdownData, setBreakdownData] = React.useState(null);
  const [breakdownLoading, setBreakdownLoading] = React.useState(false);

  const monthKey = format(month, "yyyy-MM");
  const monthStart = format(month, "yyyy-MM-01");
  const monthEnd = (() => {
    const d = addMonths(month, 1);
    const last = new Date(d.getFullYear(), d.getMonth(), 0);
    return format(last, "yyyy-MM-dd");
  })();

  const [form, setForm] = React.useState({
    date: format(new Date(), "yyyy-MM-dd"),
    category: "Utilities",
    description: "",
    amount: "",
  });
  const [editingId, setEditingId] = React.useState(null);

  const load = React.useCallback(async () => {
    setLoading(true);
    try {
      const [s, e] = await Promise.all([
        api.get(`/finance/summary?month=${monthKey}`),
        api.get(`/expenses?start=${monthStart}&end=${monthEnd}`),
      ]);
      setSummary(s.data);
      setExpenses(e.data || []);
    } catch (err) {
      toast.error("Failed to load finance data");
    } finally {
      setLoading(false);
    }
  }, [monthKey, monthStart, monthEnd]);

  React.useEffect(() => { load(); }, [load]);

  const openBreakdown = async (type, title) => {
    setBreakdown({ type, title });
    if (breakdownData?.month === monthKey) return; // cached for this month
    setBreakdownLoading(true);
    try {
      const r = await api.get(`/finance/breakdown?month=${monthKey}`);
      setBreakdownData({ ...r.data, month: monthKey });
    } catch (err) {
      toast.error("Failed to load breakdown");
      setBreakdown(null);
    } finally {
      setBreakdownLoading(false);
    }
  };
  // Reset cache whenever month changes
  React.useEffect(() => {
    setBreakdownData(null);
  }, [monthKey]);

  const resetForm = () => {
    setForm({
      date: format(new Date(), "yyyy-MM-dd"),
      category: "Utilities",
      description: "",
      amount: "",
    });
    setEditingId(null);
  };

  const submit = async (e) => {
    e.preventDefault();
    const amount = Number(form.amount);
    if (!form.date) return toast.error("Pick a date");
    if (!Number.isFinite(amount) || amount < 0) return toast.error("Amount must be a positive number");
    try {
      const payload = {
        date: form.date,
        category: form.category,
        description: form.description,
        amount,
      };
      if (editingId) {
        await api.put(`/expenses/${editingId}`, payload);
        toast.success("Expense updated");
      } else {
        await api.post(`/expenses`, payload);
        toast.success("Expense added");
      }
      resetForm();
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Save failed");
    }
  };

  const edit = (e) => {
    setEditingId(e.id);
    setForm({
      date: e.date,
      category: e.category,
      description: e.description,
      amount: String(e.amount),
    });
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this expense?")) return;
    await api.delete(`/expenses/${id}`);
    toast.success("Deleted");
    load();
  };

  const [financeSyncing, setFinanceSyncing] = React.useState(false);
  const [sheetsAvailable, setSheetsAvailable] = React.useState(false);
  const [sheetUrl, setSheetUrl] = React.useState("");
  React.useEffect(() => {
    api
      .get("/admin/sheets/status")
      .then((r) => {
        setSheetsAvailable(!!r.data?.configured);
        if (r.data?.sheet_id) {
          setSheetUrl(`https://docs.google.com/spreadsheets/d/${r.data.sheet_id}/edit#gid=0`);
        }
      })
      .catch(() => {});
  }, []);

  const syncFinance = async () => {
    setFinanceSyncing(true);
    try {
      const r = await api.post("/admin/finance/sync");
      if (r.data?.ok) {
        toast.success(`Finance ledger synced — ${r.data.rows} transactions written to Google Sheets`);
      } else {
        toast.error(r.data?.reason || "Sync failed");
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Sync failed");
    } finally {
      setFinanceSyncing(false);
    }
  };

  const profit = (summary?.net_income ?? 0) >= 0;

  return (
    <div data-testid="admin-finance-page">
      <div className="flex items-end justify-between mb-8 flex-wrap gap-4">
        <div>
          <p className="overline mb-2">Finance</p>
          <h1 className="font-serif-display text-4xl">Income &amp; Expenses</h1>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          {sheetsAvailable && sheetUrl && (
            <a
              href={sheetUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="h-10 px-4 rounded-full border border-[#C5A059]/40 text-[#C5A059] hover:bg-[#C5A059]/10 transition-colors text-xs uppercase tracking-[0.18em] flex items-center gap-1.5"
              data-testid="open-google-sheet-link"
              title="Open the Finance tab in your Google Sheet (new window)"
            >
              <ExternalLink size={14} />
              Open Google Sheet
            </a>
          )}
          {sheetsAvailable && (
            <button
              type="button"
              onClick={syncFinance}
              disabled={financeSyncing}
              className="h-10 px-4 rounded-full border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 transition-colors text-xs uppercase tracking-[0.18em] flex items-center gap-1.5"
              data-testid="sync-finance-button"
              title="Push every downpayment, balance collection, and expense as separate rows to the Finance tab of your Google Sheet"
            >
              <RefreshCw size={14} className={financeSyncing ? "animate-spin" : ""} />
              {financeSyncing ? "Syncing…" : "Sync Finance to Sheets"}
            </button>
          )}
          <button
            onClick={() => setMonth((m) => subMonths(m, 1))}
            className="w-10 h-10 grid place-items-center rounded-full border border-[var(--border)] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
            data-testid="finance-prev-month"
            aria-label="Previous month"
          >
            <ChevronLeft size={16} />
          </button>
          <div
            className="font-serif-display text-2xl px-4 min-w-[180px] text-center"
            data-testid="finance-current-month"
          >
            {format(month, "MMMM yyyy")}
          </div>
          <button
            onClick={() => setMonth((m) => addMonths(m, 1))}
            className="w-10 h-10 grid place-items-center rounded-full border border-[var(--border)] hover:border-[#C5A059] hover:text-[#C5A059] transition-colors"
            data-testid="finance-next-month"
            aria-label="Next month"
          >
            <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mb-8">
        <KpiCard
          label="Gross Income"
          sub="Cash recognised · this month"
          value={formatPHP(summary?.gross_income || 0)}
          accent="#C5A059"
          testid="kpi-gross"
          onClick={() => openBreakdown("gross_income", "Gross Income · Cash recognised this month")}
        />
        <KpiCard
          label="Expenses"
          sub={`${summary?.expenses_count || 0} entries`}
          value={formatPHP(summary?.expenses_total || 0)}
          accent="#FF6B6B"
          testid="kpi-expenses"
          onClick={() => openBreakdown("expenses", "Expenses · Itemized")}
        />
        <KpiCard
          label="Net Income"
          sub={profit ? "Profit" : "Loss"}
          value={formatPHP(summary?.net_income || 0)}
          accent={profit ? "#34D399" : "#FF6B6B"}
          testid="kpi-net"
          onClick={() => openBreakdown("net_income", "Net Income · Income vs Expenses")}
        />
      </div>

      {/* Secondary stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
        <SecondaryStat
          label="Confirmed Bookings"
          value={summary?.bookings_confirmed || 0}
          dot="bg-emerald-400"
          onClick={() => openBreakdown("confirmed", "Confirmed Bookings · This Month")}
        />
        <SecondaryStat
          label="Pending"
          value={summary?.bookings_pending || 0}
          dot="bg-amber-400"
          onClick={() => openBreakdown("pending", "Pending Bookings · This Month")}
        />
        <SecondaryStat
          label="Downpayments Received"
          value={formatPHP(summary?.downpayments_received || 0)}
          onClick={() => openBreakdown("downpayments_all", "Downpayments Received · This Month")}
        />
        <SecondaryStat
          label="Pending Revenue"
          value={formatPHP(summary?.pending_revenue || 0)}
          onClick={() => openBreakdown("pending", "Pending Revenue · Awaiting Confirmation")}
        />
      </div>

      {/* Cash on Hand */}
      <div
        className="border border-[#C5A059]/40 rounded-2xl p-6 mb-10 bg-gradient-to-br from-[#C5A059]/10 to-[#13151D]"
        data-testid="cash-on-hand-card"
      >
        <button
          type="button"
          onClick={() => openBreakdown("cash_on_hand", "Cash on Hand · Money Received")}
          className="text-left w-full mb-4 group"
          data-testid="cash-on-hand-clickable"
        >
          <p className="overline text-[#C5A059] mb-1">Cash on Hand · {format(month, "MMMM yyyy")}</p>
          <h3 className="font-serif-display text-3xl group-hover:text-[#C5A059] transition-colors">
            {formatPHP(summary?.cash_on_hand || 0)}
          </h3>
          <p className="text-xs text-[#A1A4B5] mt-1.5">
            Click to see every booking contributing to this total. Includes full payments for completed stays + downpayments for upcoming/in-progress bookings.
          </p>
        </button>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <CashRow
            label="Already Arrived (full payment)"
            sub="Guest checked in today or earlier"
            value={summary?.cash_full_payments || 0}
            data-testid="cash-full"
            onClick={() => openBreakdown("cash_full", "Already Arrived · Full Payment Collected")}
          />
          <CashRow
            label="Downpayments Only"
            sub="Upcoming or in-progress bookings"
            value={summary?.cash_downpayments_only || 0}
            data-testid="cash-downpayments"
            onClick={() => openBreakdown("cash_dp_only", "Downpayments Only · Upcoming Bookings")}
          />
          <CashRow
            label="Balance Still Due"
            sub="Will be collected on guest arrival"
            value={summary?.upcoming_balance_due || 0}
            muted
            data-testid="cash-balance-due"
            onClick={() => openBreakdown("balance_due", "Balance Still Due · Collect on Arrival")}
          />
        </div>
      </div>

      {/* Income & expense breakdown */}
      <div className="grid md:grid-cols-2 gap-5 mb-10">
        <div className="border border-[var(--border)] rounded-2xl p-5 bg-[#13151D]">
          <h3 className="font-serif-display text-xl mb-4">Income by Booking Type</h3>
          <BreakdownRow label="Day Use" amount={summary?.income_by_type?.day_use || 0} count={summary?.count_by_type?.day_use || 0} dot="bg-amber-400" />
          <BreakdownRow label="Night Use" amount={summary?.income_by_type?.night_use || 0} count={summary?.count_by_type?.night_use || 0} dot="bg-indigo-400" />
          <BreakdownRow label="Overnight Use" amount={summary?.income_by_type?.whole_day || 0} count={summary?.count_by_type?.whole_day || 0} dot="bg-rose-400" />
        </div>
        <div className="border border-[var(--border)] rounded-2xl p-5 bg-[#13151D]">
          <h3 className="font-serif-display text-xl mb-4">Expenses by Category</h3>
          {Object.keys(summary?.expenses_by_category || {}).length === 0 ? (
            <p className="text-[#A1A4B5] text-sm">No expenses logged for this month.</p>
          ) : (
            Object.entries(summary.expenses_by_category).map(([cat, amt]) => (
              <BreakdownRow key={cat} label={cat} amount={amt} dot="bg-rose-400" />
            ))
          )}
        </div>
      </div>

      {/* Expense form */}
      <form
        onSubmit={submit}
        className="border border-[var(--border)] rounded-2xl p-6 mb-8 bg-[#13151D]"
        data-testid="expense-form"
      >
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-serif-display text-xl">
            {editingId ? "Edit Expense" : "Log New Expense"}
          </h3>
          {editingId && (
            <button
              type="button"
              onClick={resetForm}
              className="text-[#A1A4B5] hover:text-white text-xs flex items-center gap-1"
            >
              <X size={14} /> Cancel edit
            </button>
          )}
        </div>
        <div className="grid md:grid-cols-4 gap-4">
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Date</Label>
            <Input
              type="date"
              value={form.date}
              onChange={(e) => setForm({ ...form, date: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="expense-date-input"
              required
            />
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Category</Label>
            <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
              <SelectTrigger
                className="mt-2 bg-[#0A0B10] border-[var(--border)]"
                data-testid="expense-category-select"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent className="bg-[#13151D] border-[var(--border)]">
                {CATEGORIES.map((c) => (
                  <SelectItem key={c} value={c}>{c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Amount (PHP)</Label>
            <Input
              type="number"
              step="0.01"
              min="0"
              value={form.amount}
              onChange={(e) => setForm({ ...form, amount: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)]"
              data-testid="expense-amount-input"
              required
            />
          </div>
          <div className="md:row-span-2">
            <Label className="text-[#A1A4B5] text-xs uppercase tracking-[0.18em]">Description</Label>
            <Textarea
              value={form.description}
              onChange={(e) => setForm({ ...form, description: e.target.value })}
              className="mt-2 bg-[#0A0B10] border-[var(--border)] min-h-[44px]"
              rows={2}
              placeholder="e.g. Pool maintenance — chlorine refill"
              data-testid="expense-description-input"
            />
          </div>
        </div>
        <Button
          type="submit"
          className="gold-btn rounded-full mt-6 px-6"
          data-testid="expense-save-button"
        >
          <Plus size={14} className="mr-2" /> {editingId ? "Update Expense" : "Add Expense"}
        </Button>
      </form>

      {/* Expense list */}
      <div className="border border-[var(--border)] rounded-2xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--border)] hover:bg-transparent">
              <TableHead className="text-[#A1A4B5]">Date</TableHead>
              <TableHead className="text-[#A1A4B5]">Category</TableHead>
              <TableHead className="text-[#A1A4B5]">Description</TableHead>
              <TableHead className="text-[#A1A4B5] text-right">Amount</TableHead>
              <TableHead className="text-[#A1A4B5] text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#A1A4B5] py-8">Loading…</TableCell></TableRow>
            )}
            {!loading && expenses.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-[#A1A4B5] py-8">No expenses logged for this month.</TableCell></TableRow>
            )}
            {expenses.map((e) => (
              <TableRow key={e.id} className="border-[var(--border)]" data-testid={`expense-row-${e.id}`}>
                <TableCell>{formatPHTDate(e.date)}</TableCell>
                <TableCell className="text-[#C5A059]">{e.category}</TableCell>
                <TableCell className="text-[#A1A4B5] max-w-md truncate">{e.description || "—"}</TableCell>
                <TableCell className="text-right font-medium">{formatPHP(e.amount)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button
                      size="sm"
                      variant="outline"
                      className="bg-transparent border-[var(--border)]"
                      onClick={() => edit(e)}
                      data-testid={`expense-edit-${e.id}`}
                    >
                      <Pencil size={14} />
                    </Button>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={() => remove(e.id)}
                      data-testid={`expense-delete-${e.id}`}
                    >
                      <Trash2 size={14} />
                    </Button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <BreakdownDialog
        open={!!breakdown}
        onClose={() => setBreakdown(null)}
        type={breakdown?.type}
        title={breakdown?.title}
        loading={breakdownLoading}
        data={breakdownData}
        summary={summary}
        monthLabel={format(month, "MMMM yyyy")}
      />
    </div>
  );
}

function KpiCard({ label, sub, value, accent, testid, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`relative border border-[var(--border)] rounded-2xl p-6 bg-[#13151D] overflow-hidden text-left w-full ${
        onClick ? "hover:border-[#C5A059]/60 hover:bg-[#15171F] transition-colors cursor-pointer" : ""
      }`}
      data-testid={testid}
    >
      <div className="absolute inset-y-0 left-0 w-1" style={{ background: accent }} />
      <div className="text-[10px] uppercase tracking-[0.22em] text-[#A1A4B5]">{label}</div>
      <div className="font-serif-display text-3xl mt-3" style={{ color: accent }}>
        {value}
      </div>
      <div className="text-[11px] text-[#A1A4B5] mt-2 flex items-center justify-between">
        <span>{sub}</span>
        {onClick && <span className="text-[#C5A059]/60 text-[10px]">View →</span>}
      </div>
    </Tag>
  );
}

function SecondaryStat({ label, value, dot, onClick }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`border border-[var(--border)] rounded-xl p-4 bg-[#13151D] text-left w-full ${
        onClick ? "hover:border-[#C5A059]/60 hover:bg-[#15171F] transition-colors cursor-pointer" : ""
      }`}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5] flex items-center gap-1.5">
        {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />} {label}
      </div>
      <div className="font-serif-display text-xl mt-2">{value}</div>
    </Tag>
  );
}

function BreakdownRow({ label, amount, count, dot }) {
  return (
    <div className="flex items-center justify-between py-2 border-b border-[var(--border)] last:border-0">
      <div className="flex items-center gap-2">
        {dot && <span className={`w-2 h-2 rounded-full ${dot}`} />}
        <span className="text-sm text-white">{label}</span>
        {typeof count === "number" && (
          <span className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5]">
            · {count} {count === 1 ? "booking" : "bookings"}
          </span>
        )}
      </div>
      <div className="text-sm text-[#C5A059] font-medium">{formatPHP(amount)}</div>
    </div>
  );
}

function CashRow({ label, sub, value, muted = false, onClick, "data-testid": testid }) {
  const Tag = onClick ? "button" : "div";
  return (
    <Tag
      type={onClick ? "button" : undefined}
      onClick={onClick}
      className={`rounded-xl border border-[var(--border)] bg-[#0A0B10]/60 p-4 text-left w-full ${
        onClick ? "hover:border-[#C5A059]/60 hover:bg-[#0A0B10] transition-colors cursor-pointer" : ""
      }`}
      data-testid={testid}
    >
      <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5] flex items-center justify-between">
        <span>{label}</span>
        {onClick && <span className="text-[#C5A059]/60 text-[10px]">View →</span>}
      </div>
      <div className={`font-serif-display text-xl mt-1.5 ${muted ? "text-[#A1A4B5]" : "text-white"}`}>
        {formatPHP(value)}
      </div>
      <div className="text-[10px] text-[#A1A4B5] mt-1">{sub}</div>
    </Tag>
  );
}



const TYPE_LABEL = {
  day_use: "Day Use",
  night_use: "Night Use",
  whole_day: "Overnight Use",
};
const STATUS_BADGE = {
  pending: "bg-yellow-500/20 text-yellow-300 border-yellow-500/40",
  confirmed: "bg-emerald-500/20 text-emerald-300 border-emerald-500/40",
  cancelled: "bg-red-500/20 text-red-300 border-red-500/40",
  on_hold: "bg-orange-500/20 text-orange-300 border-orange-500/40",
};

function filterBookingsForType(type, allBookings) {
  if (!allBookings) return [];
  switch (type) {
    case "gross_income":
      // Income recognised this month (downpayment slice if booking was created this month
      // + balance slice if the stay is this month). Hide rows that contributed 0.
      return allBookings.filter((b) => b.status === "confirmed" && (b.cash_this_month || 0) > 0);
    case "confirmed":
      return allBookings.filter((b) => b.status === "confirmed");
    case "pending":
      return allBookings.filter((b) => b.status === "pending");
    case "downpayments_all":
      // Downpayments RECEIVED this month — created this month with a downpayment amount
      return allBookings.filter(
        (b) => ["confirmed", "pending"].includes(b.status) && (b.downpayment_amount || 0) > 0
          && (b.cash_kind === "downpayment" || b.cash_kind === "full")
      );
    case "cash_on_hand":
      return allBookings.filter((b) => b.cash_contribution > 0);
    case "cash_full":
      return allBookings.filter((b) => b.status === "confirmed" && b.is_completed);
    case "cash_dp_only":
      return allBookings.filter(
        (b) =>
          (b.status === "confirmed" && !b.is_completed && b.downpayment_amount > 0) ||
          (b.status === "pending" && b.downpayment_amount > 0)
      );
    case "balance_due":
      return allBookings.filter((b) => b.balance_due > 0);
    default:
      return [];
  }
}

function BreakdownDialog({ open, onClose, type, title, loading, data, summary, monthLabel }) {
  const isExpenses = type === "expenses";
  const isNetIncome = type === "net_income";
  const bookingsForType = data ? filterBookingsForType(type, data.bookings) : [];

  // Pick the right "amount column" per breakdown type
  const amountFor = (b) => {
    switch (type) {
      case "gross_income":
        // Show only the slice recognised in THIS month (downpayment, balance, or full)
        return b.cash_this_month ?? b.total_price;
      case "confirmed":
        return b.total_price;
      case "downpayments_all":
        return b.downpayment_amount;
      case "cash_full":
        return b.total_price;
      case "cash_dp_only":
        return b.downpayment_amount;
      case "balance_due":
        return b.balance_due;
      case "cash_on_hand":
        return b.cash_contribution;
      case "pending":
        return b.total_price;
      default:
        return b.total_price;
    }
  };

  const total = bookingsForType.reduce((s, b) => s + (amountFor(b) || 0), 0);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent
        className="bg-[#0A0B10] border-[var(--border)] max-w-4xl max-h-[90vh] overflow-y-auto"
        data-testid="breakdown-dialog"
      >
        <DialogHeader>
          <DialogTitle className="font-serif-display text-2xl">{title}</DialogTitle>
          <DialogDescription className="text-[#A1A4B5] text-xs">
            {monthLabel} · {loading ? "Loading…" : isExpenses ? `${data?.expenses?.length || 0} entries` : isNetIncome ? "Income vs expenses" : `${bookingsForType.length} booking${bookingsForType.length === 1 ? "" : "s"}`}
          </DialogDescription>
        </DialogHeader>

        {loading && <p className="text-center text-[#A1A4B5] py-8 text-sm">Loading breakdown…</p>}

        {!loading && isNetIncome && summary && (
          <div className="space-y-4">
            <NetIncomeSummary summary={summary} />
          </div>
        )}

        {!loading && isExpenses && (
          <ExpensesBreakdownTable expenses={data?.expenses || []} />
        )}

        {!loading && !isExpenses && !isNetIncome && (
          <BookingBreakdownTable
            bookings={bookingsForType}
            amountFor={amountFor}
            total={total}
            type={type}
          />
        )}
      </DialogContent>
    </Dialog>
  );
}

function NetIncomeSummary({ summary }) {
  const net = summary.net_income || 0;
  const profit = net >= 0;
  return (
    <div className="space-y-3">
      <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/5 p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5]">Income (Confirmed)</div>
        <div className="font-serif-display text-2xl text-emerald-300 mt-1">+ {formatPHP(summary.gross_income || 0)}</div>
      </div>
      <div className="rounded-xl border border-rose-500/30 bg-rose-500/5 p-4">
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5]">Expenses ({summary.expenses_count || 0} entries)</div>
        <div className="font-serif-display text-2xl text-rose-300 mt-1">− {formatPHP(summary.expenses_total || 0)}</div>
      </div>
      <div className={`rounded-xl border p-4 ${profit ? "border-emerald-500/40 bg-emerald-500/10" : "border-rose-500/40 bg-rose-500/10"}`}>
        <div className="text-[10px] uppercase tracking-[0.18em] text-[#A1A4B5]">Net Income · {profit ? "Profit" : "Loss"}</div>
        <div className={`font-serif-display text-3xl mt-1 ${profit ? "text-emerald-300" : "text-rose-300"}`}>
          {formatPHP(net)}
        </div>
      </div>
    </div>
  );
}

function BookingBreakdownTable({ bookings, amountFor, total, type }) {
  if (!bookings || bookings.length === 0) {
    return <p className="text-center text-[#A1A4B5] py-8 text-sm">No bookings in this category for this month.</p>;
  }
  return (
    <div>
      <div className="border border-[var(--border)] rounded-xl overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="border-[var(--border)] hover:bg-transparent">
              <TableHead className="text-[#A1A4B5]">Date</TableHead>
              <TableHead className="text-[#A1A4B5]">Guest</TableHead>
              <TableHead className="text-[#A1A4B5]">Type</TableHead>
              <TableHead className="text-[#A1A4B5]">Status</TableHead>
              <TableHead className="text-[#A1A4B5] text-right">Amount</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {bookings.map((b) => (
              <TableRow key={b.id} className="border-[var(--border)]">
                <TableCell className="text-sm">
                  {formatPHTDate(b.booking_date)}
                  {b.end_date && b.end_date !== b.booking_date && (
                    <div className="text-[10px] text-[#A1A4B5]">→ {formatPHTDate(b.end_date)}</div>
                  )}
                </TableCell>
                <TableCell className="text-sm font-medium">{b.guest_name}</TableCell>
                <TableCell className="text-sm">{TYPE_LABEL[b.booking_type] || b.booking_type}</TableCell>
                <TableCell>
                  <Badge className={`${STATUS_BADGE[b.status]} border capitalize text-[10px]`}>
                    {(b.status || "").replace("_", " ")}
                  </Badge>
                  {type === "cash_full" && b.is_completed && (
                    <div className="text-[9px] text-emerald-400 mt-0.5">guest arrived</div>
                  )}
                  {type === "gross_income" && b.cash_kind && b.cash_kind !== "none" && (
                    <div
                      className="text-[9px] mt-0.5 text-[#C5A059] uppercase tracking-[0.16em]"
                      data-testid={`cash-kind-${b.id}`}
                    >
                      {b.cash_kind === "full"
                        ? "full · same month"
                        : b.cash_kind === "downpayment"
                        ? "downpayment slice"
                        : "balance slice"}
                    </div>
                  )}
                </TableCell>
                <TableCell className="text-right font-medium text-[#C5A059]">
                  {formatPHP(amountFor(b))}
                </TableCell>
              </TableRow>
            ))}
            <TableRow className="border-[var(--border)] bg-[#13151D]">
              <TableCell colSpan={4} className="font-medium uppercase text-xs tracking-[0.18em] text-[#A1A4B5]">Total</TableCell>
              <TableCell className="text-right font-serif-display text-lg text-[#C5A059]">
                {formatPHP(total)}
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

function ExpensesBreakdownTable({ expenses }) {
  if (!expenses || expenses.length === 0) {
    return <p className="text-center text-[#A1A4B5] py-8 text-sm">No expenses logged this month.</p>;
  }
  const total = expenses.reduce((s, e) => s + (e.amount || 0), 0);
  return (
    <div className="border border-[var(--border)] rounded-xl overflow-hidden">
      <Table>
        <TableHeader>
          <TableRow className="border-[var(--border)] hover:bg-transparent">
            <TableHead className="text-[#A1A4B5]">Date</TableHead>
            <TableHead className="text-[#A1A4B5]">Category</TableHead>
            <TableHead className="text-[#A1A4B5]">Description</TableHead>
            <TableHead className="text-[#A1A4B5] text-right">Amount</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {expenses.map((e) => (
            <TableRow key={e.id} className="border-[var(--border)]">
              <TableCell className="text-sm">{formatPHTDate(e.date)}</TableCell>
              <TableCell className="text-sm">{e.category}</TableCell>
              <TableCell className="text-sm text-[#A1A4B5]">{e.description || "—"}</TableCell>
              <TableCell className="text-right font-medium text-rose-300">{formatPHP(e.amount)}</TableCell>
            </TableRow>
          ))}
          <TableRow className="border-[var(--border)] bg-[#13151D]">
            <TableCell colSpan={3} className="font-medium uppercase text-xs tracking-[0.18em] text-[#A1A4B5]">Total</TableCell>
            <TableCell className="text-right font-serif-display text-lg text-rose-300">
              {formatPHP(total)}
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>
    </div>
  );
}
